import { useEffect, useMemo, useRef, useState, useLayoutEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, isDesigner } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "@/components/ui/dialog";
import { PhaseFormDialog } from "@/components/shared/PhaseFormDialog";
import TaskDetailModal from "@/components/TaskDetailModal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { PageHeader, StatCard, useIsMobile } from "@/components/shared/PagePrimitives";
import { FilterBar } from "@/components/shared/FilterBar";
import { FilterPanel } from "@/components/shared/FilterPanel";
import { AlertTriangle, ChevronDown, ChevronRight, CalendarClock, Layers, Users, Plus, RotateCw } from "lucide-react";
import { toDate, fmtISO, addDays, diffDays, startOfDay, addWorkingDays, todayISO } from "@/lib/days";
import { ask } from "@/lib/confirm";

const DONE = ["Dokončeno", "Fakturováno", "Záruka"];
const CZ_MONTHS = ["Led", "Úno", "Bře", "Dub", "Kvě", "Čvn", "Čvc", "Srp", "Zář", "Říj", "Lis", "Pro"];
const CZ_DOW = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
const PRESETS = { week: 26, month: 9, quarter: 3.4 };
const MIN_PX = 2, MAX_PX = 60;

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const ROW_H = 44;
const HEADER_H = 52;
const MIN_BAR = 8;
const LABEL_INSIDE_MIN = 64;

const PROJECT_HUES = [24, 200, 150, 280, 340, 45, 190, 100];

function groupMeta(phases) {
  if (!phases.length) return null;
  const start = fmtISO(new Date(phases.reduce((m, p) => Math.min(m, +toDate(p.start_date)), Infinity)));
  const end = fmtISO(new Date(phases.reduce((m, p) => Math.max(m, +toDate(p.end_date)), -Infinity)));
  const days = diffDays(toDate(end), toDate(start)) + 1;
  const dur = days >= 14 ? `${Math.round(days / 7)} týd.` : `${days} dní`;
  return { start, end, days, dur };
}

export default function Planning() {
  const nav = useNavigate();
  const { user } = useAuth();
  // Projektant (173) Gantt jen čte: žádné tažení, vytváření, úpravy ani potvrzování kolizí.
  const canEdit = !isDesigner(user);
  const [data, setData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pxPerDay, setPxPerDay] = useState(PRESETS.month);
  const [projectFilter, setProjectFilter] = useState("all");
  const [personFilter, setPersonFilter] = useState("all");
  const [activeOnly, setActiveOnly] = useState(true);
  const mobile = useIsMobile();
  const [collapsed, setCollapsed] = useState({});
  const [showAcked, setShowAcked] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [progOpen, setProgOpen] = useState(null);   // { g, value } — % hotovo zakázky bez fází
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({});
  const [draft, setDraft] = useState(null);
  const [createGhost, setCreateGhost] = useState(null);
  const [phaseTasks, setPhaseTasks] = useState([]);
  const [taskModal, setTaskModal] = useState(null);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", assignees: [], deadline: "", priority: "Střední" });
  const [labelW, setLabelW] = useState(typeof window !== "undefined" && window.innerWidth < 640 ? 128 : 224);
  const scrollRef = useRef(null);
  const [scrollEl, setScrollEl] = useState(null);
  const attachScrollEl = useCallback((el) => { scrollRef.current = el; setScrollEl(el); }, []);
  const dragRef = useRef(null);
  const curDraftRef = useRef(null);
  const movedRef = useRef(false);
  const pxRef = useRef(pxPerDay);
  const anchorRef = useRef(null);
  const geomRef = useRef({ min: null });
  const createRef = useRef(null);
  const creatingRef = useRef(false);
  const lpTimer = useRef(null);

  useEffect(() => { pxRef.current = pxPerDay; }, [pxPerDay]);
  useEffect(() => {
    const onR = () => setLabelW(window.innerWidth < 640 ? 128 : 224);
    window.addEventListener("resize", onR); return () => window.removeEventListener("resize", onR);
  }, []);
  // 218: každé načtení Ganttu má pořadové číslo — starší odpověď (třeba obnova na pozadí
  // po předchozím tahu) nikdy nepřepíše novější data.
  const ganttSeq = useRef(0);
  const reloadGantt = useCallback(async () => {
    const s = ++ganttSeq.current;
    const r = await api.get("/planning/gantt");
    if (s === ganttSeq.current) { setData(r.data); setLoadFailed(false); }
  }, []);
  // Po tahu se nové termíny ukážou hned; kolize se dopočítají na pozadí.
  const refreshGantt = () => { reloadGantt().catch(() => {}); };
  const firstLoad = useCallback(() => { setLoadFailed(false); reloadGantt().catch(() => setLoadFailed(true)); }, [reloadGantt]);
  useEffect(() => { firstLoad(); }, [firstLoad]);
  // Místní úprava termínů (fáze / zakázka) z odpovědi serveru — bez čekání na celý Gantt.
  const patchDates = (projectId, phaseDates, project) => setData((d) => d && {
    ...d,
    projects: d.projects.map((p) => {
      if (p.id !== projectId) return p;
      const byId = Object.fromEntries((phaseDates || []).map((x) => [x.id, x]));
      return {
        ...p,
        ...(project ? { start_date: (project.start_date || "").slice(0, 10) || null, end_date: (project.end_date || "").slice(0, 10) || null } : {}),
        phases: p.phases.map((ph) => (byId[ph.id] ? { ...ph, start_date: byId[ph.id].start_date, end_date: byId[ph.id].end_date } : ph))
          .sort((a, b) => ((a.start_date || "") < (b.start_date || "") ? -1 : (a.start_date || "") > (b.start_date || "") ? 1 : 0)),
      };
    }),
  });

  const collisionIds = useMemo(() => new Set(data?.collision_ids || []), [data]);

  const groups = useMemo(() => {
    if (!data) return [];
    let projects = data.projects;
    if (activeOnly) projects = projects.filter((p) => !DONE.includes(p.status));
    if (projectFilter !== "all") projects = projects.filter((p) => p.id === projectFilter);
    return projects
      .map((p) => {
        let phases = p.phases;
        if (personFilter !== "all") phases = phases.filter((ph) => (ph.responsible_ids || (ph.responsible_id ? [ph.responsible_id] : [])).includes(personFilter));
        return { ...p, phases };
      })
      .filter((p) => personFilter === "all" || p.phases.length > 0);
  }, [data, activeOnly, projectFilter, personFilter]);

  const collisions = useMemo(() => {
    if (!data) return [];
    return data.collisions.filter((c) => {
      if (personFilter !== "all" && c.person_id !== personFilter) return false;
      if (projectFilter !== "all" && c.a.project_id !== projectFilter && c.b.project_id !== projectFilter) return false;
      return true;
    });
  }, [data, personFilter, projectFilter]);

  const range = useMemo(() => {
    const allPhases = groups.flatMap((g) => g.phases);
    // Do rozsahu osy patří i od–do zakázek — sbalený nebo prázdný řádek kreslí
    // pruh zakázky a ten musí být vidět.
    const spans = [...allPhases.map((p) => [p.start_date, p.end_date]),
      ...groups.filter((g) => g.start_date && g.end_date).map((g) => [g.start_date, g.end_date])];
    let min, max;
    if (!spans.length) {
      const t = startOfDay(new Date());
      min = addDays(t, -7);
      max = addDays(t, 35);
    } else {
      min = new Date(spans.reduce((m, [s]) => Math.min(m, +toDate(s)), Infinity));
      max = new Date(spans.reduce((m, [, e]) => Math.max(m, +toDate(e)), -Infinity));
    }
    min = addDays(min, -3); max = addDays(max, 3);
    return { min, max };
  }, [groups]);

  const geometry = useMemo(() => {
    if (!range) return null;
    const { min, max } = range;
    const span = Math.max(diffDays(max, min), 1);
    const width = span * pxPerDay;
    const dayMode = pxPerDay >= 16;
    const weekMode = !dayMode && pxPerDay >= 5;
    const today = new Date();
    const todayX = diffDays(today, min) * pxPerDay;

    const months = [];
    let mIt = new Date(min.getFullYear(), min.getMonth(), 1);
    while (mIt <= max) {
      const x = diffDays(mIt, min) * pxPerDay;
      if (x >= 0) months.push({ key: +mIt, x, label: `${CZ_MONTHS[mIt.getMonth()]} ${mIt.getFullYear()}` });
      mIt = new Date(mIt.getFullYear(), mIt.getMonth() + 1, 1);
    }
    const days = [];
    if (dayMode) {
      const labelStep = Math.max(1, Math.ceil(30 / pxPerDay));
      let di = 0;
      for (let t = startOfDay(min); t <= max; t = addDays(t, 1), di++) {
        const dow = t.getDay();
        days.push({ key: +t, x: diffDays(t, min) * pxPerDay, w: pxPerDay, weekend: dow === 0 || dow === 6,
          label: `${CZ_DOW[dow]} ${t.getDate()}`, showLabel: di % labelStep === 0 });
      }
    }
    const weeks = [];
    if (weekMode) {
      let t = startOfDay(min);
      while (t.getDay() !== 1) t = addDays(t, 1);
      const stepW = Math.max(1, Math.ceil(46 / (7 * pxPerDay)));
      let wi = 0;
      for (; t <= max; t = addDays(t, 7), wi++) {
        if (wi % stepW === 0) weeks.push({ key: +t, x: diffDays(t, min) * pxPerDay, label: `${t.getDate()}.${t.getMonth() + 1}.` });
      }
    }
    return { min, max, span, width, dayMode, weekMode, todayX, months, days, weeks };
  }, [range, pxPerDay]);

  useEffect(() => { if (geometry) geomRef.current = { min: geometry.min }; }, [geometry]);

  const xOf = (iso) => geometry ? diffDays(toDate(iso), geometry.min) * pxPerDay : 0;

  const scrollToToday = () => {
    if (!geometry || !scrollRef.current) return;
    const el = scrollRef.current;
    el.scrollTo({ left: Math.max(geometry.todayX + labelW - el.clientWidth / 2, 0), behavior: "smooth" });
  };
  const scrolledRef = useRef(false);
  useEffect(() => {
    if (!geometry || scrolledRef.current) return;
    const t = setTimeout(() => { scrollToToday(); scrolledRef.current = true; }, 300);
    return () => clearTimeout(t);
  }, [geometry]); // eslint-disable-line

  // apply zoom anchor after px change
  useLayoutEffect(() => {
    if (!anchorRef.current || !scrollRef.current) return;
    const { day, offsetX } = anchorRef.current;
    scrollRef.current.scrollLeft = Math.max(labelW + day * pxPerDay - offsetX, 0);
    anchorRef.current = null;
  }, [pxPerDay, labelW]);

  const applyZoom = useCallback((next, offsetX) => {
    const el = scrollRef.current; if (!el) return;
    const cur = pxRef.current;
    const contentX = el.scrollLeft + offsetX;
    const day = (contentX - labelW) / cur;
    anchorRef.current = { day, offsetX };
    setPxPerDay(clamp(next, MIN_PX, MAX_PX));
  }, [labelW]);

  // native wheel + touch listeners for zoom/pan
  useEffect(() => {
    const el = scrollEl; if (!el) return;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      applyZoom(pxRef.current * Math.exp(-e.deltaY * 0.0015), e.clientX - rect.left);
    };
    let pinch = null, pan = null;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });
    const onTS = (e) => {
      if (e.touches.length === 2) {
        const rect = el.getBoundingClientRect();
        const m = mid(e.touches);
        const contentX = el.scrollLeft + (m.x - rect.left);
        pinch = { startDist: dist(e.touches), startPx: pxRef.current, day: (contentX - labelW) / pxRef.current };
        pan = null;
      } else if (e.touches.length === 1) {
        pan = { x: e.touches[0].clientX, y: e.touches[0].clientY, sl: el.scrollLeft, st: el.scrollTop };
        pinch = null;
      }
    };
    const onTM = (e) => {
      if (creatingRef.current) return;
      if (pinch && e.touches.length === 2) {
        e.preventDefault();
        const rect = el.getBoundingClientRect();
        const m = mid(e.touches);
        const ratio = dist(e.touches) / pinch.startDist;
        anchorRef.current = { day: pinch.day, offsetX: m.x - rect.left };
        setPxPerDay(clamp(pinch.startPx * ratio, MIN_PX, MAX_PX));
      } else if (pan && e.touches.length === 1) {
        e.preventDefault();
        el.scrollLeft = pan.sl - (e.touches[0].clientX - pan.x);
        el.scrollTop = pan.st - (e.touches[0].clientY - pan.y);
      }
    };
    const onTE = (e) => { if (e.touches.length === 0) { pinch = null; pan = null; } };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTS, { passive: false });
    el.addEventListener("touchmove", onTM, { passive: false });
    el.addEventListener("touchend", onTE);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTS);
      el.removeEventListener("touchmove", onTM);
      el.removeEventListener("touchend", onTE);
    };
  }, [applyZoom, labelW, scrollEl]);

  const tweenTo = (target) => {
    const el = scrollRef.current;
    const start = pxRef.current, t0 = performance.now(), dur = 260;
    const offsetX = el ? el.clientWidth / 2 : 300;
    const step = (now) => {
      const k = Math.min((now - t0) / dur, 1);
      const e = 1 - Math.pow(1 - k, 3);
      applyZoom(start + (target - start) * e, offsetX);
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const nearestPreset = useMemo(() => {
    let best = "month", bd = Infinity;
    for (const [k, v] of Object.entries(PRESETS)) { const d = Math.abs(Math.log(pxPerDay / v)); if (d < bd) { bd = d; best = k; } }
    return best;
  }, [pxPerDay]);

  const toggleGroup = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const savePhaseDates = async (ph, projectId, start_date, end_date) => {
    try {
      await api.patch(`/projects/${projectId}/phases/${ph.id}`, {
        name: ph.name, start_date, end_date,
        progress: ph.progress || 0, depends_on: ph.depends_on || null,
        responsible_ids: ph.responsible_ids || (ph.responsible_id ? [ph.responsible_id] : []),
      });
      patchDates(projectId, [{ id: ph.id, start_date, end_date }]);
      refreshGantt();
      toast.success(`Termíny fáze „${ph.name}" upraveny`);
    } catch { toast.error("Uložení termínů se nezdařilo"); }
    finally { setDraft(null); }
  };

  // Tažení sbaleného pruhu zakázky: celý pruh = posun (délka drží, posunou se
  // fáze i termíny nedokončených úkolů), okraj = změna od–do s kompresí fází.
  const saveProjectShift = async (g, days) => {
    try {
      const { data: r } = await api.post(`/projects/${g.id}/schedule-shift`, { days });
      if (r.phase_dates) { patchDates(g.id, r.phase_dates, r.project); refreshGantt(); } else await reloadGantt();
      const w = Math.abs(days) === 1 ? "den" : Math.abs(days) < 5 ? "dny" : "dní";
      toast.success(`„${g.name}" posunuta o ${Math.abs(days)} ${w} (${r.phases} fází, ${r.tasks} úkolů).`);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Posun se nezdařil"); }
    finally { setDraft(null); }
  };
  const saveProjectResize = async (g, start_date, end_date) => {
    try {
      const { data: r } = await api.post(`/projects/${g.id}/schedule-resize`, { start_date, end_date });
      if (r.phase_dates) { patchDates(g.id, r.phase_dates, r.project); refreshGantt(); } else await reloadGantt();
      toast.success(r.phases_changed ? `Termíny „${g.name}" upraveny — zkráceno ${r.phases_changed} fází.` : `Termíny „${g.name}" upraveny.`);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Uložení se nezdařilo"); }
    finally { setDraft(null); }
  };

  const saveProgress = async () => {
    if (!progOpen) return;
    try {
      await api.post(`/projects/${progOpen.g.id}/progress`, { progress: progOpen.value });
      await reloadGantt();
      setProgOpen(null); toast.success("Průběh zakázky uložen.");
    } catch { toast.error("Uložení se nezdařilo"); }
  };
  const startProjectDrag = (e, g, mode, s0, e0) => {
    if (!canEdit) return;
    if (e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    movedRef.current = false;
    const cur = pxRef.current;
    const key = `proj:${g.id}`;
    dragRef.current = { id: key, g, mode, startX: e.clientX, start: toDate(s0), end: toDate(e0) };
    const move = (ev) => {
      const d = dragRef.current; if (!d) return;
      const deltaDays = Math.round((ev.clientX - d.startX) / cur);
      if (deltaDays !== 0) movedRef.current = true;
      let s = new Date(d.start), en = new Date(d.end);
      if (d.mode === "move") { s = addDays(d.start, deltaDays); en = addDays(d.end, deltaDays); }
      else if (d.mode === "start") { s = addDays(d.start, deltaDays); if (s > d.end) s = d.end; }
      else if (d.mode === "end") { en = addDays(d.end, deltaDays); if (en < d.start) en = d.start; }
      const nd = { id: key, start: fmtISO(s), end: fmtISO(en) };
      const prev = curDraftRef.current;
      if (prev && prev.id === nd.id && prev.start === nd.start && prev.end === nd.end) return;
      curDraftRef.current = nd; setDraft(nd);
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      const d = dragRef.current, c = curDraftRef.current;
      dragRef.current = null; curDraftRef.current = null;
      // Stejně jako u fází: draft drží pruh na novém místě, dokud nedorazí
      // přenačtená data — jinak by na okamžik skočil zpět.
      if (!d || !c || c.id !== key || !movedRef.current) { setDraft(null); return; }
      if (d.mode === "move") {
        const days = diffDays(toDate(c.start), d.start);
        if (days !== 0) saveProjectShift(d.g, days); else setDraft(null);
      } else if (c.start !== fmtISO(d.start) || c.end !== fmtISO(d.end)) {
        saveProjectResize(d.g, c.start, c.end);
      } else setDraft(null);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const startDrag = (e, ph, projectId, mode) => {
    if (!canEdit) return;
    if (e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    movedRef.current = false;
    const cur = pxRef.current;
    dragRef.current = { id: ph.id, projectId, mode, startX: e.clientX, start: toDate(ph.start_date), end: toDate(ph.end_date), ph };
    const move = (ev) => {
      const d = dragRef.current; if (!d) return;
      const deltaDays = Math.round((ev.clientX - d.startX) / cur);
      if (deltaDays !== 0) movedRef.current = true;
      let s = new Date(d.start), en = new Date(d.end);
      if (d.mode === "move") { s = addDays(d.start, deltaDays); en = addDays(d.end, deltaDays); }
      else if (d.mode === "start") { s = addDays(d.start, deltaDays); if (s > addDays(d.end, -1)) s = addDays(d.end, -1); }
      else if (d.mode === "end") { en = addDays(d.end, deltaDays); if (en < addDays(d.start, 1)) en = addDays(d.start, 1); }
      const nd = { id: d.id, start: fmtISO(s), end: fmtISO(en) };
      const prev = curDraftRef.current;
      if (prev && prev.id === nd.id && prev.start === nd.start && prev.end === nd.end) return;
      curDraftRef.current = nd; setDraft(nd);
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      const d = dragRef.current, c = curDraftRef.current;
      dragRef.current = null; curDraftRef.current = null;
      // Draft se NEČISTÍ hned — pruh by na okamžik skočil zpět na staré místo,
      // než dorazí přenačtená data. Uklidí ho save funkce až po setData.
      if (d && c && c.id === d.id && movedRef.current && (c.start !== fmtISO(d.start) || c.end !== fmtISO(d.end)))
        savePhaseDates(d.ph, d.projectId, c.start, c.end);
      else setDraft(null);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  // 218: rovnou úkoly fáze (server filtr phase_id umí), ne všechny úkoly zakázky
  const loadPhaseTasks = (projectId, phaseId) =>
    api.get(`/tasks?project_id=${projectId}&phase_id=${phaseId}`).then((r) => setPhaseTasks(r.data)).catch(() => setPhaseTasks([]));

  const openEdit = (ph, g) => {
    if (!canEdit) return;
    setEditForm({
      id: ph.id, projectId: g.id, projectName: g.name, name: ph.name || "",
      start_date: (ph.start_date || "").slice(0, 10), end_date: (ph.end_date || "").slice(0, 10),
      responsible_ids: ph.responsible_ids || (ph.responsible_id ? [ph.responsible_id] : []),
      depends_on: ph.depends_on || "", progress: ph.progress || 0,
    });
    setPhaseTasks([]);
    loadPhaseTasks(g.id, ph.id);
    setEditOpen(true);
  };
  const openCreate = (g, start_date, end_date) => {
    if (!canEdit) return;
    setCreateForm({ projectId: g.id, projectName: g.name, name: "", start_date, end_date, responsible_ids: [], depends_on: "", progress: 0 });
    setCreateOpen(true);
  };
  const openCreateBlank = () => {
    if (!canEdit) return;
    const s = todayISO();
    setCreateForm({ projectId: "", projectName: "", name: "", start_date: s, end_date: addWorkingDays(s, 5), responsible_ids: [], depends_on: "", progress: 0 });
    setCreateOpen(true);
  };
  const onCreateProjectChange = (pid) => {
    const p = data?.projects.find((x) => x.id === pid);
    setCreateForm((f) => ({ ...f, projectId: pid, projectName: p?.name || "", depends_on: "" }));
  };
  const submitPhase = async (form, isCreate) => {
    if (isCreate && !form.projectId) return toast.error("Vyberte zakázku.");
    if (!form.name || !form.start_date || !form.end_date) return toast.error("Vyplňte název a termíny.");
    if (!(form.responsible_ids || []).length) return toast.error("Vyberte aspoň jednoho člověka.");
    try {
      const payload = { name: form.name, start_date: form.start_date, end_date: form.end_date,
        progress: Number(form.progress) || 0, depends_on: form.depends_on || null,
        responsible_ids: form.responsible_ids };
      if (isCreate) await api.post(`/projects/${form.projectId}/phases`, payload);
      else await api.patch(`/projects/${form.projectId}/phases/${form.id}`, payload);
      await reloadGantt();
      toast.success(isCreate ? "Fáze vytvořena." : "Fáze upravena.");
      if (isCreate) setCreateOpen(false); else setEditOpen(false);
    } catch { toast.error("Uložení se nezdařilo"); }
  };

  const dayToISO = (d) => fmtISO(addDays(geometry.min, Math.max(0, d)));
  const finishCreate = (c) => {
    creatingRef.current = false; setCreateGhost(null);
    if (!c || !c.g) return;
    if (c.moved && c.range) openCreate(c.g, c.range.start, c.range.end);
    else { const s = dayToISO(c.startDay); openCreate(c.g, s, addWorkingDays(s, 5)); }
  };
  const onCreateDown = (e, g, rowKey) => {
    if (!canEdit) return;
    if (e.button !== 0 || !geometry) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const startDay = Math.round((e.clientX - rect.left) / pxRef.current);
    createRef.current = { g, rowKey, startDay, moved: false, range: null };
    const move = (ev) => {
      const c = createRef.current; if (!c) return;
      const endDay = Math.round((ev.clientX - rect.left) / pxRef.current);
      if (Math.abs(endDay - c.startDay) >= 1) c.moved = true;
      const a = Math.min(c.startDay, endDay), b = Math.max(c.startDay, endDay);
      c.range = { start: dayToISO(a), end: dayToISO(b) };
      setCreateGhost({ rowKey, ...c.range });
    };
    const up = () => {
      document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up);
      const c = createRef.current; createRef.current = null; finishCreate(c);
    };
    document.addEventListener("mousemove", move); document.addEventListener("mouseup", up);
  };
  const onCreateTouchStart = (e, g, rowKey) => {
    if (!canEdit) return;
    if (e.touches.length !== 1 || !geometry) return;
    const t = e.touches[0], sx = t.clientX, sy = t.clientY;
    const rect = e.currentTarget.getBoundingClientRect();
    const startDay = Math.round((sx - rect.left) / pxRef.current);
    const cleanup = () => { document.removeEventListener("touchmove", move); document.removeEventListener("touchend", end); };
    const move = (ev) => {
      const tt = ev.touches[0]; if (!tt) return;
      if (!creatingRef.current) {
        if (Math.abs(tt.clientX - sx) > 10 || Math.abs(tt.clientY - sy) > 10) { clearTimeout(lpTimer.current); cleanup(); }
        return;
      }
      ev.preventDefault(); ev.stopPropagation();
      const c = createRef.current;
      const endDay = Math.round((tt.clientX - rect.left) / pxRef.current);
      if (Math.abs(endDay - c.startDay) >= 1) c.moved = true;
      const a = Math.min(c.startDay, endDay), b = Math.max(c.startDay, endDay);
      c.range = { start: dayToISO(a), end: dayToISO(b) };
      setCreateGhost({ rowKey, ...c.range });
    };
    const end = () => { clearTimeout(lpTimer.current); const c = createRef.current; createRef.current = null; cleanup(); finishCreate(c); };
    lpTimer.current = setTimeout(() => {
      creatingRef.current = true;
      createRef.current = { g, rowKey, startDay, moved: false, range: null };
      setCreateGhost({ rowKey, start: dayToISO(startDay), end: dayToISO(startDay) });
    }, 400);
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", end);
  };

  const openFromCollision = (x) => {
    if (x.type !== "phase") return;
    const grp = data.projects.find((p) => p.id === x.project_id);
    const ph = grp?.phases.find((p) => p.id === x.id);
    if (ph) openEdit(ph, grp);
  };

  const ackCollision = async (c) => {
    try {
      await api.post("/planning/collisions/ack", { person_id: c.person_id, a_id: c.a.id, b_id: c.b.id });
      await reloadGantt();
      toast.success("Souběh akceptován. Když se termíny pohnou, kolize se ozve znovu.");
    } catch { toast.error("Akceptace se nezdařila"); }
  };
  const unackCollision = async (c) => {
    try {
      await api.post("/planning/collisions/unack", { key: c.key });
      await reloadGantt();
    } catch { toast.error("Zrušení se nezdařilo"); }
  };

  if (!data) {
    // 218: výpadek sítě nesmí nechat stránku navždy „Načítám…“
    if (loadFailed) return (
      <div className="bg-card border border-border rounded-[16px] p-5 md:p-6 flex flex-col sm:flex-row items-center gap-3 sm:justify-between" data-testid="planning-error">
        <p className="text-sm text-muted-foreground text-center sm:text-left">Plán se nepodařilo načíst. Zkontroluj připojení a zkus to znovu.</p>
        <Button variant="outline" onClick={firstLoad} className="h-10 w-full sm:w-auto font-bold" data-testid="planning-retry"><RotateCw size={15} /> Zkusit znovu</Button>
      </div>
    );
    return <div className="text-muted-foreground" data-testid="planning-loading">Načítám…</div>;
  }

  const editPhases = editForm.projectId ? (data.projects.find((p) => p.id === editForm.projectId)?.phases || []) : [];
  const createPhases = createForm.projectId ? (data.projects.find((p) => p.id === createForm.projectId)?.phases || []) : [];

  const totalPhases = groups.reduce((s, g) => s + g.phases.length, 0);
  const respName = (uid) => (data.people || []).find((p) => p.id === uid)?.name;

  // flatten rows for aligned label + timeline
  const rows = [];
  groups.forEach((g, gi) => {
    rows.push({ type: "group", g, gi });
    if (!collapsed[g.id]) g.phases.forEach((ph) => rows.push({ type: "phase", ph, g }));
  });
  const rowsH = rows.length * ROW_H;

  // Chipy aktivních filtrů (jednotná lišta 114): Zakázka, Osoba, Vč. neaktivních.
  // Měřítko je ovládání zobrazení (trailing), ne filtr.
  const planChipDefs = [
    projectFilter !== "all" && { label: data.projects.find((p) => p.id === projectFilter)?.name || "Zakázka", clear: () => setProjectFilter("all") },
    personFilter !== "all" && { label: (data.people || []).find((p) => p.id === personFilter)?.name || "Osoba", clear: () => setPersonFilter("all") },
    !activeOnly && { label: "Vč. neaktivních", clear: () => setActiveOnly(true) },
  ].filter(Boolean);
  const planChips = planChipDefs.map((c, i) => ({ key: `plan-${i}`, label: c.label, clear: c.clear, testid: `chip-plan-${i}` }));
  const clearPlanFilters = () => { setProjectFilter("all"); setPersonFilter("all"); setActiveOnly(true); };
  const scaleSwitcher = (
    <div className="inline-flex h-11 p-1 rounded-full bg-card border border-border" data-testid="planning-scale-switcher">
      {[["week", "Týden"], ["month", "Měsíc"], ["quarter", "Kvartál"]].map(([v, l]) => (
        <button key={v} type="button" onClick={() => tweenTo(PRESETS[v])} data-testid={`planning-scale-${v}`}
          className={`h-full px-3 text-sm font-bold rounded-full transition-colors ${nearestPreset === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
      ))}
    </div>
  );


  return (
    <div className="space-y-6" data-testid="planning-page">
      <PageHeader title="Plánování" subtitle={canEdit ? "Táhni fázi nebo sbalený pruh zakázky pro posun termínů. Zoom: Ctrl+kolečko / gesto dvěma prsty. Tažením v prázdném řádku založíš novou fázi." : "Jen pro čtení — termíny upravuje vedení. Zoom: Ctrl+kolečko / gesto dvěma prsty."}
        actions={<>
          {canEdit && <Button onClick={openCreateBlank} className="h-11 font-bold" data-testid="planning-new-phase-btn"><Plus size={16} /> Nová fáze</Button>}
          <Button onClick={scrollToToday} variant="outline" className="h-11" data-testid="planning-today-btn"><CalendarClock size={16} /> Dnes</Button>
        </>} />

      <div className={`grid grid-cols-2 gap-3 ${canEdit ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        <StatCard icon={Layers} label="Zakázky v plánu" value={groups.length} testid="planning-stat-projects" />
        <StatCard icon={CalendarClock} label="Fází celkem" value={totalPhases} testid="planning-stat-phases" />
        <StatCard icon={Users} label="Osob s termíny" value={(data.people || []).length} testid="planning-stat-people" />
        {canEdit && <StatCard icon={AlertTriangle} label="Kolize termínů" value={collisions.filter((c) => !c.acked).length} danger valueClass={collisions.some((c) => !c.acked) ? "text-destructive" : ""} testid="planning-stat-collisions" />}
      </div>

      {canEdit && collisions.some((c) => !c.acked) && (
        <div className="rounded-[20px] border border-amber-500/40 bg-amber-500/10 p-4" data-testid="planning-collision-card">
          <div className="flex items-center gap-2 mb-3 text-amber-600 dark:text-amber-400 font-bold">
            <AlertTriangle size={18} /> Kolize termínů — stejná osoba na dvou stavbách zároveň ({collisions.filter((c) => !c.acked).length})
          </div>
          <div className="space-y-2">
            {collisions.filter((c) => !c.acked).map((c, i) => (
              <div key={i} className="rounded-[14px] bg-card border border-border p-3 text-sm" data-testid={`collision-row-${i}`}>
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <div className="font-semibold flex items-center gap-2"><Users size={14} /> {c.person_name}</div>
                  <div className="text-xs text-muted-foreground">{formatDate(c.overlap_start)} – {formatDate(c.overlap_end)} · {c.working_days} prac. dní</div>
                </div>
                <div className="mt-2 flex flex-col sm:flex-row gap-2">
                  {[c.a, c.b].map((x, k) => (
                    <button key={k} onClick={() => openFromCollision(x)} data-testid={`collision-${i}-item-${k}`}
                      className="flex-1 text-left rounded-[10px] bg-secondary/60 hover:bg-secondary px-3 py-2 transition-colors">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{x.type === "phase" ? "Fáze" : "Úkol"} · {x.project_name}</div>
                      <div className="font-semibold">{x.name}</div>
                    </button>
                  ))}
                </div>
                {canEdit && <div className="mt-2 flex justify-end">
                  <Button variant="outline" onClick={() => ackCollision(c)} data-testid={`collision-ack-${i}`}
                    className="h-9 w-full sm:w-auto font-bold text-[13px]">Akceptovat — plánovaný souběh</Button>
                </div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vědomé souběhy — akceptované kolize; propadnou, jakmile se termíny pohnou */}
      {canEdit && collisions.some((c) => c.acked) && (
        <div className="rounded-[20px] border border-border bg-card p-4" data-testid="planning-acked-card">
          <button onClick={() => setShowAcked((v) => !v)} data-testid="planning-acked-toggle"
            className="flex items-center gap-2 font-bold text-sm text-muted-foreground w-full text-left">
            {showAcked ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            Vědomé souběhy ({collisions.filter((c) => c.acked).length}) — akceptováno, hlídá se změna termínů
          </button>
          {showAcked && (
            <div className="space-y-2 mt-3">
              {collisions.filter((c) => c.acked).map((c, i) => (
                <div key={i} className="rounded-[14px] border border-border p-3 text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`acked-row-${i}`}>
                  <span className="min-w-0">
                    <span className="block font-semibold">{c.person_name} · {c.a.project_name} + {c.b.project_name}</span>
                    <span className="block text-xs text-muted-foreground">{formatDate(c.overlap_start)} – {formatDate(c.overlap_end)} · akceptoval {c.acked_by || "—"}</span>
                  </span>
                  {canEdit && <button onClick={() => unackCollision(c)} data-testid={`acked-unack-${i}`}
                    className="text-[13px] font-bold text-muted-foreground hover:text-destructive shrink-0">Zrušit akceptaci</button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Jednotná lišta filtrů (zadání 114) — Měřítko jako rychlý přepínač vpravo. */}
      <div className="relative">
        <FilterBar testid="planning-filterbar" trailing={scaleSwitcher}
          filterCount={planChipDefs.length} onFilterClick={() => setPanelOpen((o) => !o)}
          chips={planChips} onClearAll={clearPlanFilters} />
        {panelOpen && (
          <FilterPanel open={panelOpen} onClose={() => setPanelOpen(false)} mobile={mobile} width={680}
            filterCount={planChipDefs.length} resultCount={groups.length}
            plural={(n) => (n === 1 ? "zakázka" : n >= 2 && n <= 4 ? "zakázky" : "zakázek")}
            values={{ project: projectFilter, person: personFilter, includeInactive: !activeOnly }}
            setValue={(key, val) => {
              if (key === "project") return setProjectFilter(val);
              if (key === "person") return setPersonFilter(val);
              if (key === "includeInactive") return setActiveOnly(!val);
            }}
            clear={clearPlanFilters}
            groups={[
              { key: "project", label: "Zakázka", layout: "list",
                options: [{ value: "all", label: "Všechny zakázky" }, ...data.projects.map((p) => ({ value: p.id, label: p.name }))] },
              { key: "person", label: "Osoba", layout: "list", avatar: true,
                options: [{ value: "all", label: "Všechny osoby" }, ...(data.people || []).map((p) => ({ value: p.id, label: p.name, photo: p.photo_url }))] },
              { key: "active", label: "Zobrazit", layout: "toggles",
                options: [{ value: "includeInactive", label: "Vč. neaktivních zakázek" }] },
            ]} />
        )}
      </div>

      {!geometry || groups.length === 0 ? (
        <div className="bg-card border border-border rounded-[20px] p-10 text-center text-muted-foreground space-y-4" data-testid="planning-empty">
          <p>Zatím žádné zakázky k plánování{personFilter !== "all" ? " pro zvolenou osobu" : ""}. Vytvořte první fázi.</p>
          {canEdit && <Button onClick={openCreateBlank} className="h-11 font-bold" data-testid="planning-empty-new-phase-btn"><Plus size={16} /> Nová fáze</Button>}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-[20px] overflow-hidden" data-testid="planning-gantt">
          <div ref={attachScrollEl} className="overflow-auto max-h-[72vh]" style={{ touchAction: "none" }}>
            <div className="relative" style={{ width: labelW + geometry.width }}>
              {/* background layer over timeline area */}
              <div className="absolute pointer-events-none" style={{ left: labelW, top: HEADER_H, width: geometry.width, height: rowsH }}>
                {geometry.dayMode && geometry.days.filter((d) => d.weekend).map((d) => (
                  <div key={`we-${d.key}`} className="absolute top-0 bottom-0 bg-muted-foreground/10" style={{ left: d.x, width: d.w }} />
                ))}
                {geometry.months.map((m) => <div key={m.key} className="absolute top-0 bottom-0 w-px bg-border" style={{ left: m.x }} />)}
                {geometry.todayX >= 0 && geometry.todayX <= geometry.width && (
                  <div className="absolute top-0 bottom-0 w-0.5 bg-brand" style={{ left: geometry.todayX }} />
                )}
              </div>

              {/* header row (sticky top) */}
              <div className="flex sticky top-0 z-20" style={{ height: HEADER_H }}>
                <div className="sticky left-0 z-30 bg-card border-r border-b border-border flex items-end px-3 pb-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-muted-foreground shrink-0" style={{ width: labelW }}>Zakázka / fáze</div>
                <div className="relative bg-card border-b border-border" style={{ width: geometry.width }}>
                  <div className="relative h-6">
                    {geometry.months.map((m) => <span key={m.key} className="absolute top-1 text-[11px] font-bold uppercase tracking-wide whitespace-nowrap" style={{ left: m.x + 4 }}>{m.label}</span>)}
                  </div>
                  <div className="relative h-6">
                    {geometry.dayMode ? geometry.days.filter((d) => d.showLabel).map((d) => (
                      <span key={d.key} className={`absolute top-0 -translate-x-1/2 text-[10px] whitespace-nowrap ${d.weekend ? "text-muted-foreground/70 font-semibold" : "text-muted-foreground"}`} style={{ left: d.x + d.w / 2 }}>{d.label}</span>
                    )) : geometry.weeks.map((w) => (
                      <span key={w.key} className="absolute top-0 -translate-x-1/2 text-[10px] text-muted-foreground whitespace-nowrap" style={{ left: w.x }}>{w.label}</span>
                    ))}
                    {geometry.todayX >= 0 && geometry.todayX <= geometry.width && (
                      <span className="absolute top-0 -translate-x-1/2 text-[10px] text-brand font-bold" style={{ left: geometry.todayX }}>dnes</span>
                    )}
                  </div>
                </div>
              </div>

              {/* rows */}
              {rows.map((row, ri) => {
                if (row.type === "group") {
                  const g = row.g, meta = groupMeta(g.phases);
                  const gCol = g.phases.some((ph) => collisionIds.has(ph.id));
                  const hue = PROJECT_HUES[row.gi % PROJECT_HUES.length];
                  return (
                    <div key={g.id} className="flex" style={{ height: ROW_H }}>
                      <button onClick={() => toggleGroup(g.id)} data-testid={`planning-group-${g.id}`}
                        className="sticky left-0 z-10 bg-card border-r border-b border-border/60 flex items-center justify-between gap-1 px-2 sm:px-3 font-bold text-xs sm:text-sm text-left hover:bg-secondary/60 transition-colors shrink-0" style={{ width: labelW }}>
                        <span className="flex items-center gap-1 min-w-0">
                          {collapsed[g.id] ? <ChevronRight size={15} className="shrink-0" /> : <ChevronDown size={15} className="shrink-0" />}
                          <span className="truncate">{g.name}</span>
                        </span>
                        {meta && <span className="text-[9px] sm:text-[10px] font-semibold text-muted-foreground shrink-0">{meta.dur}</span>}
                      </button>
                      <div className="relative border-b border-border/60 bg-secondary/10" style={{ width: geometry.width }}
                        onMouseDown={(ev) => onCreateDown(ev, g, `g-${g.id}`)} onTouchStart={(ev) => onCreateTouchStart(ev, g, `g-${g.id}`)}>
                        {(collapsed[g.id] || !g.phases.length) && (g.start_date && g.end_date ? true : !!meta) && (() => {
                          // Pruh zakázky: přednostně od–do zakázky (to, co se vyplňuje
                          // při založení); fallback na rozsah fází u starých zakázek.
                          const canDrag = canEdit && !!(g.start_date && g.end_date);
                          const key = `proj:${g.id}`;
                          const dragging = draft && draft.id === key;
                          const s = dragging ? draft.start : (canDrag ? g.start_date : meta.start);
                          const e = dragging ? draft.end : (canDrag ? g.end_date : meta.end);
                          const left = xOf(s), w = Math.max(xOf(e) - left + pxPerDay, MIN_BAR);
                          const inside = w >= LABEL_INSIDE_MIN;
                          const days = diffDays(toDate(e), toDate(s)) + 1;
                          const dw = days === 1 ? "den" : days < 5 ? "dny" : "dní";
                          return (
                            <>
                              <div
                                onMouseDown={canDrag ? (ev) => startProjectDrag(ev, g, "move", s, e) : (ev) => ev.stopPropagation()}
                                onClick={() => { if (movedRef.current) { movedRef.current = false; return; } if (canEdit && !g.phases.length) setProgOpen({ g, value: g.progress || 0 }); else toggleGroup(g.id); }}
                                data-testid={`planning-summary-${g.id}`}
                                title={canDrag ? "Táhni pro posun celé zakázky (fáze a úkoly jedou s ní) · okraje mění od–do" : "Zakázka nemá vyplněné od–do"}
                                className={`absolute top-1/2 -translate-y-1/2 h-8 rounded-sm border overflow-hidden flex items-center px-2 ${canDrag ? "cursor-grab active:cursor-grabbing" : ""} ${gCol ? "border-amber-500" : "border-border"} ${dragging ? "ring-2 ring-brand z-30" : ""}`}
                                style={{ left, width: w, background: `hsl(${hue} 45% 45% / 0.25)` }}>
                                {!g.phases.length && (g.progress || 0) > 0 && (
                                  <div className="absolute inset-y-0 left-0 pointer-events-none bg-brand/30" style={{ width: `${Math.min(g.progress, 100)}%` }} />
                                )}
                                {inside && <span className="relative text-xs font-semibold truncate flex items-center gap-1 pointer-events-none">{gCol && <AlertTriangle size={11} className="text-amber-500" />}{g.name} · {formatDate(s)}–{formatDate(e)} · {days} {dw}{!g.phases.length ? ` · ${g.progress || 0}%` : ""}</span>}
                                {canDrag && <span onMouseDown={(ev) => startProjectDrag(ev, g, "start", s, e)} data-testid={`planning-summary-resize-start-${g.id}`} className="absolute left-0 inset-y-0 w-2 cursor-ew-resize bg-brand/30 hover:bg-brand" />}
                                {canDrag && <span onMouseDown={(ev) => startProjectDrag(ev, g, "end", s, e)} data-testid={`planning-summary-resize-end-${g.id}`} className="absolute right-0 inset-y-0 w-2 cursor-ew-resize bg-brand/30 hover:bg-brand" />}
                              </div>
                              {!inside && <span className="absolute top-1/2 -translate-y-1/2 whitespace-nowrap text-xs font-semibold pointer-events-none flex items-center gap-1" style={{ left: left + w + 6 }}>{gCol && <AlertTriangle size={11} className="text-amber-500" />}{g.name}</span>}
                              {dragging && (
                                <div className="absolute -top-1 z-40 text-[10px] font-bold bg-brand text-white px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none" style={{ left }}>
                                  {formatDate(draft.start)} – {formatDate(draft.end)}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        {createGhost && createGhost.rowKey === `g-${g.id}` && <CreateGhost ghost={createGhost} xOf={xOf} pxPerDay={pxPerDay} />}
                      </div>
                    </div>
                  );
                }
                const { ph, g } = row;
                const dragging = draft && draft.id === ph.id;
                const s = dragging ? draft.start : ph.start_date, e = dragging ? draft.end : ph.end_date;
                const left = xOf(s), w = Math.max(xOf(e) - left + pxPerDay, MIN_BAR);
                const inside = w >= LABEL_INSIDE_MIN;
                const collision = collisionIds.has(ph.id);
                const prog = ph.progress || 0, done = prog >= 100;
                const label = `${ph.name} · ${prog}%`;
                return (
                  <div key={ph.id} className="flex" style={{ height: ROW_H }}>
                    <div className="sticky left-0 z-10 bg-card border-r border-b border-border/40 flex items-center gap-1 pl-6 sm:pl-8 pr-2 text-xs sm:text-sm text-muted-foreground shrink-0" style={{ width: labelW }} data-testid={`planning-phaselabel-${ph.id}`}>
                      {collision && <AlertTriangle size={12} className="text-amber-500 shrink-0" />}
                      <span className="truncate">{ph.name}</span>
                    </div>
                    <div className="relative border-b border-border/40" style={{ width: geometry.width }}
                      onMouseDown={(ev) => onCreateDown(ev, g, `p-${ph.id}`)} onTouchStart={(ev) => onCreateTouchStart(ev, g, `p-${ph.id}`)}>
                      <div
                        onMouseDown={(ev) => startDrag(ev, ph, g.id, "move")}
                        onClick={() => { if (movedRef.current) { movedRef.current = false; return; } openEdit(ph, g); }}
                        data-testid={`planning-bar-${ph.id}`} title="Táhni pro posun · okraje pro změnu začátku/konce"
                        className={`group absolute top-1/2 -translate-y-1/2 h-8 rounded-sm border overflow-hidden flex items-center px-2 cursor-grab active:cursor-grabbing ${done ? "bg-emerald-500/15 border-emerald-500" : collision ? "bg-secondary border-amber-500" : "bg-secondary border-border"} ${dragging ? "ring-2 ring-brand z-30" : ""}`}
                        style={{ left, width: w }}>
                        <div className={`absolute inset-y-0 left-0 pointer-events-none ${done ? "bg-emerald-500/40" : "bg-brand/30"}`} style={{ width: `${Math.min(prog, 100)}%` }} />
                        {inside && <span className="relative text-xs font-semibold truncate pointer-events-none flex items-center gap-1">{collision && <AlertTriangle size={11} className="text-amber-500" />}{label}</span>}
                        <span onMouseDown={(ev) => startDrag(ev, ph, g.id, "start")} data-testid={`planning-resize-start-${ph.id}`} className="absolute left-0 inset-y-0 w-2 cursor-ew-resize bg-brand/30 hover:bg-brand" />
                        <span onMouseDown={(ev) => startDrag(ev, ph, g.id, "end")} data-testid={`planning-resize-end-${ph.id}`} className="absolute right-0 inset-y-0 w-2 cursor-ew-resize bg-brand/30 hover:bg-brand" />
                      </div>
                      {!inside && !dragging && (
                        <span className="absolute top-1/2 -translate-y-1/2 whitespace-nowrap text-xs font-semibold pointer-events-none flex items-center gap-1" style={{ left: left + w + 6 }}>
                          {collision && <AlertTriangle size={11} className="text-amber-500" />}{label}
                        </span>
                      )}
                      {dragging && (
                        <div className="absolute -top-1 z-40 text-[10px] font-bold bg-brand text-white px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none" style={{ left }}>
                          {formatDate(draft.start)} – {formatDate(draft.end)}
                        </div>
                      )}
                      {createGhost && createGhost.rowKey === `p-${ph.id}` && <CreateGhost ghost={createGhost} xOf={xOf} pxPerDay={pxPerDay} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <PhaseFormDialog open={editOpen} onOpenChange={setEditOpen} mode="edit"
        projectName={editForm.projectName} form={editForm} setForm={setEditForm}
        responsibles={data.responsibles || []} phases={editPhases}
        tasks={phaseTasks.filter((t) => t.phase_id === editForm.id)}
        taskStats={(() => {
          const list = phaseTasks.filter((t) => t.phase_id === editForm.id);
          return list.length ? { total: list.length, done: list.filter((t) => ["Hotovo", "Schváleno"].includes(t.status)).length } : undefined;
        })()}
        onOpenTask={(t) => { setEditOpen(false); setTaskModal(t.id); }}
        onAddTask={() => { setEditOpen(false); setTaskForm({ title: "", assignees: [], deadline: "", priority: "Střední" }); setAddTaskOpen(true); }}
        onNavigateProject={() => nav(`/zakazky/${editForm.projectId}?tab=schedule`)}
        onDelete={async () => {
          if (!await ask("Opravdu smazat fázi?")) return;
          try {
            await api.delete(`/projects/${editForm.projectId}/phases/${editForm.id}`);
            await reloadGantt();
            setEditOpen(false); toast.success("Fáze smazána.");
          } catch { toast.error("Smazání se nezdařilo"); }
        }}
        onSubmit={() => submitPhase(editForm, false)} />
      <PhaseFormDialog open={createOpen} onOpenChange={setCreateOpen} mode="create"
        projectName={createForm.projectName} form={createForm} setForm={setCreateForm}
        responsibles={data.responsibles || []} phases={createPhases}
        projects={data.projects} onProjectChange={onCreateProjectChange}
        onNavigateProject={createForm.projectId ? () => nav(`/zakazky/${createForm.projectId}?tab=schedule`) : undefined}
        onSubmit={() => submitPhase(createForm, true)} />

      <Dialog open={addTaskOpen} onOpenChange={setAddTaskOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="gantt-task-dialog">
          <DialogHeader><DialogTitle>Nový úkol k fázi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Název *</Label><Input data-testid="gantt-task-title" value={taskForm.title}
              onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} className="h-11 mt-1" placeholder="Např. Objednat kontejnery" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Termín</Label><Input type="date" value={taskForm.deadline}
                onChange={(e) => setTaskForm({ ...taskForm, deadline: e.target.value })} className="h-11 mt-1 w-full min-w-0" /></div>
              <div>
                <Label>Priorita</Label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                  <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Nízká", "Střední", "Vysoká"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Přiřadit</Label>
              <div className="space-y-1 mt-1 max-h-40 overflow-y-auto border border-border rounded-[14px] p-2">
                {(data.responsibles || []).map((u) => (
                  <label key={u.id} className="flex items-center gap-2 p-1.5 hover:bg-secondary rounded-sm cursor-pointer">
                    <Checkbox checked={taskForm.assignees.includes(u.id)}
                      onCheckedChange={() => setTaskForm((f) => ({ ...f, assignees: f.assignees.includes(u.id) ? f.assignees.filter((x) => x !== u.id) : [...f.assignees, u.id] }))}
                      data-testid={`gantt-task-assignee-${u.id}`} />
                    <span className="text-sm">{u.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button data-testid="gantt-task-save" className="w-full h-12 font-bold"
              onClick={async () => {
                if (!taskForm.title) return toast.error("Vyplňte název úkolu.");
                try {
                  await api.post("/tasks", { title: taskForm.title, project_id: editForm.projectId, phase_id: editForm.id,
                    assignees: taskForm.assignees, deadline: taskForm.deadline || null, priority: taskForm.priority });
                  toast.success("Úkol přidán.");
                  setAddTaskOpen(false);
                  await loadPhaseTasks(editForm.projectId, editForm.id);   // Gantt úkoly nenese (218)
                  setEditOpen(true);
                } catch { toast.error("Uložení se nezdařilo"); }
              }}>Přidat úkol</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* % hotovo zakázky bez fází — stejný posuvník jako u fází */}
      <Dialog open={!!progOpen} onOpenChange={(o) => { if (!o) setProgOpen(null); }}>
        <DialogContent className="max-w-sm" data-testid="project-progress-dialog">
          <DialogHeader><DialogTitle>{progOpen?.g.name}</DialogTitle></DialogHeader>
          {progOpen && (
            <div className="space-y-4">
              <p className="text-[13px] text-muted-foreground tabular-nums">
                {formatDate(progOpen.g.start_date)} – {formatDate(progOpen.g.end_date)}
              </p>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>V jaké fázi se zakázka nachází</Label>
                  <span className="text-[17px] font-bold tabular-nums">{progOpen.value}&nbsp;%</span>
                </div>
                <Slider value={[progOpen.value]} min={0} max={100} step={5}
                  onValueChange={(v) => setProgOpen((p) => ({ ...p, value: v[0] }))} data-testid="project-progress-slider" />
              </div>
              <Button onClick={saveProgress} className="w-full h-12 font-bold" data-testid="project-progress-save">Uložit</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TaskDetailModal taskId={taskModal} open={!!taskModal} onOpenChange={(o) => !o && setTaskModal(null)}
        onChanged={() => { if (editForm.projectId) loadPhaseTasks(editForm.projectId, editForm.id); }} />
    </div>
  );
}

function CreateGhost({ ghost, xOf, pxPerDay }) {
  const left = xOf(ghost.start);
  const w = Math.max(xOf(ghost.end) - left + pxPerDay, 6);
  return (
    <div className="absolute top-1/2 -translate-y-1/2 h-8 rounded-sm border-2 border-dashed border-brand bg-brand/20 pointer-events-none flex items-center px-2 z-40"
      style={{ left, width: w }} data-testid="planning-create-ghost">
      <span className="text-[10px] font-bold text-brand truncate whitespace-nowrap">Nová fáze · {formatDate(ghost.start)}–{formatDate(ghost.end)}</span>
    </div>
  );
}

